/**
 * useSidebarScrollMemory — keep a sidebar's own scroll offset across navigation.
 *
 * ## The row (NAV-02)
 *
 * Paul, 25 August: "the screen resets to the top here on the menu side on the
 * left... it is doing it correctly if I swap between the screens, it does it
 * correctly for the right side, but this menu side is its own scroll bar. So the
 * menu should not be reset to the top, otherwise it feels awkward."
 *
 * He is describing a REMOUNT, not a scroll. None of this app's shells is a
 * persistent layout: every screen renders its own `ScreenShell` or its own
 * `AdminLayout`, so navigating unmounts the whole shell, sidebar included, and
 * the replacement starts at zero. The content pane behaves because the browser
 * restores document scroll; a sidebar with `overflow-y: auto` has its own scroll
 * container and nothing was restoring it.
 *
 * ## Why this is a module and not three copies
 *
 * A 26 August pass fixed exactly one of the three shells, inline. The firm
 * console, which is where a firm administrator spends the day and which has the
 * longest sidebar of the three, kept resetting. One hook means the next shell
 * gets the behaviour by calling it rather than by someone remembering.
 *
 * ## Why module scope rather than a store
 *
 * This is pure view state. Nothing else in the app has any business reading it,
 * it must not be persisted, and it must NOT survive a reload: landing on a
 * freshly loaded page already scrolled is the odd behaviour, not the fix. A
 * module-level map dies with the tab, which is exactly the lifetime wanted.
 *
 * Keyed per shell, because the client sidebar and the firm sidebar hold
 * different lists of different lengths and sharing one offset between them would
 * restore a position that does not exist on the other.
 *
 * Hoisting the shells into persistent layouts is the structural fix and a large,
 * risky refactor across every screen. This is the small one.
 */

import { useLayoutEffect, useRef, type RefObject, type UIEvent } from 'react';

const offsets = new Map<string, number>();

export interface SidebarScrollMemory<T extends HTMLElement> {
  /** Attach to the scrolling element itself, the one with overflow-y: auto. */
  ref: RefObject<T | null>;
  /** Attach to the same element's onScroll. */
  onScroll: (event: UIEvent<T>) => void;
}

export function useSidebarScrollMemory<T extends HTMLElement>(
  key: string,
): SidebarScrollMemory<T> {
  const ref = useRef<T | null>(null);

  // BEFORE paint, not after. A useEffect here would let the browser paint the
  // sidebar at zero and then jump it, which is the flicker the row is about
  // wearing a different hat.
  useLayoutEffect(() => {
    const el = ref.current;
    const saved = offsets.get(key) ?? 0;
    if (el && saved > 0) el.scrollTop = saved;
    // Deliberately runs once per mount. A navigation IS a new mount here, which
    // is the whole reason this hook exists; re-running on `key` alone would do
    // nothing useful because `key` is a constant per shell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ref,
    onScroll: (event: UIEvent<T>) => {
      offsets.set(key, event.currentTarget.scrollTop);
    },
  };
}

/**
 * Test seam. Sign-out and a fresh boot both want the offsets gone, and a test
 * that asserts restoration has to be able to start from a known zero.
 */
export function resetSidebarScrollMemory(): void {
  offsets.clear();
}
